import { isNil } from 'lodash-es'
import { useEffect, useState } from 'react'
import { analytics, isTrackingDisabled } from 'lib/tracking/segment'

const FALLBACK_POLL_INTERVAL_MS = 200
const FALLBACK_POLL_TIMEOUT_MS = 5_000

/**
 * Waits for Segment's `analytics.ready()` (which resolves when all device-mode
 * destinations, including Intercom, have loaded) and then checks `window.Intercom`.
 * A short fallback poll covers the edge case where the Intercom SDK hasn't fully
 * initialised by the time the Segment plugin signals ready.
 */
export const useIntercomAvailable = (): boolean => {
  const [isIntercomAvailable, setIntercomAvailable] = useState(isTrackingDisabled() ? false : !isNil(window.Intercom))

  useEffect(() => {
    if (isIntercomAvailable || isTrackingDisabled()) {
      return
    }

    let intervalId: ReturnType<typeof setInterval>
    let timeoutId: ReturnType<typeof setTimeout>
    let cancelled = false

    analytics.ready().then(() => {
      if (cancelled) {
        return
      }

      if (!isNil(window.Intercom)) {
        setIntercomAvailable(true)
        return
      }

      intervalId = setInterval(() => {
        if (!isNil(window.Intercom)) {
          setIntercomAvailable(true)
          clearInterval(intervalId)
          clearTimeout(timeoutId)
        }
      }, FALLBACK_POLL_INTERVAL_MS)

      timeoutId = setTimeout(() => {
        clearInterval(intervalId)
      }, FALLBACK_POLL_TIMEOUT_MS)
    })

    return () => {
      cancelled = true

      if (intervalId) {
        clearInterval(intervalId)
      }

      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isIntercomAvailable])

  return isIntercomAvailable
}
